import React, { useEffect, useMemo, useState } from 'react';
import {
  makeStyles,
  shorthands,
  tokens,
  Input,
  Spinner,
  Text,
} from '@fluentui/react-components';
import { Search20Regular } from '@fluentui/react-icons';
import Header from '../components/Header';
import DataTable, { ColumnConfig } from '../components/DataTable';
import { apiFetch } from '../lib/apiFetch';

const useStyles = makeStyles({
  workspaceCard: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('8px'),
    boxShadow: tokens.shadow4,
    flexGrow: 1,
    // Floor to the viewport so flexGrow can't collapse the card to content
    // height when the nested flex height chain fails to resolve (gray gap
    // below the work surface on scroll). flexShrink:0 prevents re-squeeze.
    minHeight: 'calc(100vh - 64px)',
    flexShrink: 0,
    ...shorthands.margin('8px'),
    ...shorthands.overflow('hidden'),
    // Mobile: full-bleed work surface — drop the margin gutter and rounded
    // corners so the content uses every pixel of width.
    '@media (max-width: 768px)': {
      ...shorthands.margin('0'),
      ...shorthands.borderRadius('0'),
    },
  },
  body: {
    display: 'flex',
    alignItems: 'stretch',
    flexGrow: 1,
    minHeight: 0,
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minWidth: 0,
    minHeight: 0,
  },
  searchBar: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('12px'),
    ...shorthands.padding('8px', '12px'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    ...shorthands.padding('40px'),
    width: '100%',
  },
});

interface SaleItem {
  id: string;
  name: string;
  sku: string;
  stock_count: number;
  base_price_cents: number;
}

const formatPrice = (cents: number) =>
  `$${(cents / 100).toFixed(2)}`;

export default function SaleItems() {
  const styles = useStyles();
  const [items, setItems] = useState<SaleItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const loadSaleItems = async () => {
      try {
        setLoading(true);
        const res = await apiFetch(`/booqable/product-groups?include_hidden=true`);
        const data = await res.json();
        const mapped: SaleItem[] = (data.product_groups || [])
          .filter((item: any) => item.product_type === 'sales_item')
          .map((item: any) => ({
            id: item.id,
            name: item.name,
            sku: item.sku || 'N/A',
            stock_count: item.stock_count || 0,
            base_price_cents: item.base_price_in_cents || 0,
          }));
        setItems(mapped);
        setError(null);
      } catch (err) {
        console.error('Error fetching sale items:', err);
        setError('Failed to load sale items');
      } finally {
        setLoading(false);
      }
    };
    loadSaleItems();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(it =>
      it.name.toLowerCase().includes(q)
      || it.sku.toLowerCase().includes(q)
    );
  }, [items, search]);

  const columns: ColumnConfig<SaleItem>[] = [
    { columnKey: 'name', label: 'Name', sortable: true },
    { columnKey: 'sku', label: 'SKU', sortable: true },
    {
      columnKey: 'stock_count',
      label: 'Stock',
      sortable: true,
      render: (it) => String(it.stock_count),
    },
    {
      columnKey: 'base_price_cents',
      label: 'Price',
      sortable: true,
      render: (it) => formatPrice(it.base_price_cents),
    },
  ];

  return (
    <div className={styles.workspaceCard}>
      <Header
        inline
        customTitle={
          <span style={{
            fontSize: tokens.fontSizeBase500,
            fontWeight: tokens.fontWeightSemibold,
            lineHeight: tokens.lineHeightBase300,
            color: tokens.colorNeutralForeground1,
          }}>
            Sale Items
          </span>
        }
      />
      <div className={styles.body}>
        <div className={styles.content}>
          <div className={styles.searchBar}>
            <Input
              size="large"
              contentBefore={<Search20Regular />}
              placeholder="Filter sale items by name or SKU…"
              value={search}
              onChange={(_e, data) => setSearch(data.value)}
              style={{ flexGrow: 1, maxWidth: '480px' }}
            />
            {search.trim() && (
              <span style={{ fontSize: '13px', color: tokens.colorNeutralForeground3 }}>
                {filtered.length} of {items.length}
              </span>
            )}
          </div>
          {loading ? (
            <div className={styles.loadingContainer}>
              <Spinner label="Loading sale items..." />
            </div>
          ) : error ? (
            <div className={styles.loadingContainer}>
              <Text>{error}</Text>
            </div>
          ) : (
            <DataTable
              data={filtered}
              columns={columns}
              keyField="id"
              hideableColumns
              columnVisibilityStorageKey="sale_items"
              itemsPerPage={50}
              showCheckboxes={false}
              primaryColumnKey="name"
              disableWrapper={true}
            />
          )}
        </div>
      </div>
    </div>
  );
}
